<template>
  <CModal :auto-focus="false" show :to="attachTo" :trap-focus="false" :z-index="zIndex" @keydown.esc="saveAndClose">
    <div
      class="flex flex-col pa-4 gap-4 rounded-t-4 bg-primary6 self-end ma-0 w-full h-[calc(100vh-40px)] slide-up-enter slide-in-content slide-in-animation"
    >
      <AppHeader @close="saveAndClose()" />

      <CWarningAlert
        v-model:visible="contentWarning.isWarningVisible.value"
        :title="t('canvasUI.agendaSelector.nonPresentationContentWarning')"
      />

      <main class="flex flex-auto flex-col gap-4 overflow-auto">
        <AgendaToolbar />

        <VueDraggable
          class="flex flex-auto flex-col bg-white rounded-2 pa-4 gap-6 overflow-auto"
          handle="[data-handle-group]"
          :model-value="agenda.groups"
          v-bind="DragConfig"
          @update:model-value="updateGroups"
        >
          <AgendaGroup v-for="group in agenda.groups" :key="group.id" :filters="agenda.filters" :group="group" />
        </VueDraggable>
      </main>

      <AppFooter @cancel="cancel()" @close="saveAndClose()" />
    </div>
  </CModal>

  <MoveItemModal
    v-if="moveItemModal.isOpen.value"
    :groups="agenda.groups"
    :z-index="childModalZIndex"
    @moveToGroup="moveItemToGroup"
  />

  <ApplicationBlockSelectorModal
    v-if="applicationBlockSelectorModal.isOpen.value"
    :items="[]"
    :z-index="childModalZIndex"
  />
</template>

<script lang="ts" setup>
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueDraggable } from 'vue-draggable-plus'
import CWarningAlert from '@lib/components/CWarningAlert/CWarningAlert.vue'
import CModal from '@lib/components/CModal/CModal.vue'
import { provideAppStore } from './stores/app.store'
import useConfirmation from '@lib/composables/useConfirmation'
import AppHeader from './components/AppHeader.vue'
import AppFooter from './components/AppFooter.vue'
import ApplicationBlockSelectorModal from './components/modals/ApplicationBlockSelectorModal.vue'
import AgendaToolbar from '@agenda-selector/components/AgendaToolbar.vue'
import AgendaGroup from '@agenda-selector/components/AgendaGroup.vue'
import MoveItemModal from '@agenda-selector/components/modals/MoveItemModal.vue'
import { provideMoveItemModal } from '@agenda-selector/composables/move-item-modal.use'
import { provideApplicationBlockSelectorModal } from '@agenda-selector/composables/application-block-selector-modal.use'
import type { Agenda, AgendaSelectorProps } from '@agenda-selector/types/agenda.type'
import { provideTableInput } from '@agenda-selector/composables/table-input.use'
import { DragConfig } from '@agenda-selector/constants'

const props = defineProps<AgendaSelectorProps>()

const emit = defineEmits<{
  close: [agenda: Agenda]
  cancel: []
}>()

provideTableInput()
const { t } = useI18n()
const confirmation = useConfirmation()
const moveItemModal = provideMoveItemModal()
const applicationBlockSelectorModal = provideApplicationBlockSelectorModal()

const { agenda, moveItemToGroup, updateGroups, childModalZIndex, contentWarning } = provideAppStore({
  initialAgenda: props.initialAgenda,
  t,
  confirmation,
  selectCollectionContent: props.selectCollectionContent,
  attachTo: props.attachTo,
  zIndex: props.zIndex,
  openSettings: props.openSettings,
})

const cancel = () => emit('cancel')
const saveAndClose = () => emit('close', agenda.value)

onMounted(() => {
  contentWarning.validateAgendaContent(agenda.value)
})
</script>

<style lang="scss" scoped>
@keyframes slideInFromBottom {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.slide-in-animation {
  animation: slideInFromBottom 0.2s ease-out forwards;
}
</style>
